'use client';

import { c as _c } from "react/compiler-runtime";
import { useObservable } from '@legendapp/state/react';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext } from 'react';
import type { Observable } from '@legendapp/state';
import * as z from 'zod';
import { useActiveCountryCode } from '@/hooks/useActiveCountryCode';
import { buildObeSearchUrl } from '@/lib/buildObeSearchUrl';
import { jucyAttribution } from '@/lib/jucyAttribution';
import analytics from '@/services/analytics';
import { findSiteInRegion, getFleetTypesOptions, listSitesOptions, mapSitesToRegion } from '@/services/rental-api-client';
export const searchFormSchema = z.object({
  driverAge: z.coerce.number().optional().default(21),
  dropOffDate: z.coerce.date(),
  dropOffBranch: z.string(),
  dropOffTime: z.coerce.date().optional(),
  fleetType: z.string(),
  fleetCategoryCode: z.string().optional(),
  pickUpDate: z.coerce.date(),
  pickUpBranch: z.string(),
  pickUpTime: z.coerce.date().optional(),
  promoCode: z.string().optional(),
  reservationReference: z.string().optional()
});
export type SearchFormValues = z.infer<typeof searchFormSchema>;
export type UseCreateSearchFormStateOptions = {
  defaultValues?: Partial<SearchFormValues>;
};
function useCreateSearchFormState(t0) {
  const $ = _c(4);
  const {
    defaultValues
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getFleetTypesOptions();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: fleetTypesData,
    error: fleetTypesError
  } = useQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = listSitesOptions();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: sitesData,
    error: sitesError
  } = useQuery(t2);
  const error = fleetTypesError || sitesError;
  if (error) {
    throw error;
  }
  let t3;
  if ($[2] !== fleetTypesData) {
    t3 = fleetTypesData || [];
    $[2] = fleetTypesData;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const fleetTypes = t3;
  const sites = sitesData || [];
  const activeCountryCode = useActiveCountryCode();
  const regions = sites ? mapSitesToRegion({
    sites,
    countryCode: activeCountryCode
  }) : [];
  const state = useObservable({
    ...defaultValues,
    fleetTypes,
    regions,
    getBranchByCode: code => {
      const regions_0 = state.regions.get();
      return code ? findSiteInRegion(regions_0, code) : undefined;
    },
    fleetTypeValid: () => searchFormSchema.shape.fleetType.safeParse(state.fleetType.get()).success,
    pickUpBranchValid: () => searchFormSchema.shape.pickUpBranch.safeParse(state.getPickUpBranch()?.siteCode).success,
    dropOffBranchValid: () => searchFormSchema.shape.dropOffBranch.safeParse(state.getDropOffBranch()?.siteCode).success,
    getPickUpBranch: () => state.getBranchByCode(state.pickUpBranch.get()),
    getDropOffBranch: () => state.getBranchByCode(state.dropOffBranch.get()),
    locationsValid: () => {
      const pickUpBranchValid = state.pickUpBranchValid();
      const dropOffBranchValid = state.dropOffBranchValid();
      return Boolean(pickUpBranchValid && dropOffBranchValid);
    },
    isValid: () => {
      const {
        success
      } = searchFormSchema.safeParse(state.get());
      return success;
    },
    pickUpCountryCode: () => state.getPickUpBranch()?.countryCode || activeCountryCode,
    getSearchUrl: () => {
      const {
        success: success_0,
        data
      } = searchFormSchema.safeParse(state.get());
      const countryCode = state.pickUpCountryCode();
      if (!success_0) {
        return;
      }
      const result = buildObeSearchUrl(data, countryCode);
      if (!result.searchParams.has("pickUpTime")) {
        result.searchParams.append("pickUpTime", "10:00");
      }
      if (!result.searchParams.has("dropOffTime")) {
        result.searchParams.append("dropOffTime", "10:00");
      }
      return new URL(jucyAttribution.decorateUrl(result.href));
    },
    trackBookingInitiated: () => {
      const {
        fleetType,
        pickUpBranch,
        pickUpDate,
        dropOffBranch,
        dropOffDate,
        promoCode
      } = state.peek();
      if (!fleetType || !pickUpBranch || !pickUpDate || !dropOffBranch || !dropOffDate) {
        return;
      }
      analytics.plugins.bookingFunnel.trackProgress({
        step: "bookingInitiated",
        brand: "jucy",
        fleetType,
        pickUpLocation: pickUpBranch,
        pickUpDate,
        dropOffLocation: dropOffBranch,
        dropOffDate,
        coupon: promoCode
      });
    }
  });
  return state;
}
export type SearchFormState = ReturnType<typeof useCreateSearchFormState>;
const searchFromContext = createContext<Observable<SearchFormState>>(undefined as unknown as Observable<SearchFormState>);
export type SearchFromProviderProps = UseCreateSearchFormStateOptions & {
  children: ReactNode;
};
export function SearchFromProvider(t0) {
  const $ = _c(6);
  let children;
  let createSearchFormStateOptions;
  if ($[0] !== t0) {
    ({
      children,
      ...createSearchFormStateOptions
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = createSearchFormStateOptions;
  } else {
    children = $[1];
    createSearchFormStateOptions = $[2];
  }
  const form = useCreateSearchFormState(createSearchFormStateOptions);
  let t1;
  if ($[3] !== children || $[4] !== form) {
    t1 = <searchFromContext.Provider value={form}>{children}</searchFromContext.Provider>;
    $[3] = children;
    $[4] = form;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function useSearchFromContext() {
  const context = useContext(searchFromContext);
  if (!context) {
    throw new Error("useSearchFromContext must be used within a SearchFromProvider");
  }
  return context;
}